/**
 * The O Collection — animated hero background.
 * A Three.js full-screen shader plane simulating warm gold light moving
 * across deep Aegean water. Scroll position subtly drifts the pattern
 * and fades/parallaxes the canvas as the visitor scrolls past the hero.
 */
(function () {
  "use strict";

  var canvas = document.querySelector("[data-hero-canvas]");
  if (!canvas) return;

  var prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  if (typeof THREE === "undefined") {
    // three.js failed to load (CDN blocked, offline, etc). The hero still
    // looks intentional thanks to the CSS gradient on .hero — bail quietly.
    canvas.style.display = "none";
    return;
  }

  var VERTEX_SHADER = [
    "varying vec2 vUv;",
    "void main() {",
    "  vUv = uv;",
    "  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);",
    "}",
  ].join("\n");

  var FRAGMENT_SHADER = [
    "precision highp float;",
    "varying vec2 vUv;",
    "uniform float uTime;",
    "uniform vec2 uResolution;",
    "uniform vec3 uColorDeep;",
    "uniform vec3 uColorSapphire;",
    "uniform vec3 uColorGold;",
    "uniform float uScroll;",
    "",
    "float wave(vec2 p, float freq, float speed, float t) {",
    "  return sin(p.x * freq + t * speed) * cos(p.y * freq * 0.8 - t * speed * 0.7);",
    "}",
    "",
    "void main() {",
    "  vec2 uv = vUv;",
    "  uv.y += uScroll * 0.08;",
    "",
    "  float t = uTime * 0.06;",
    "  vec2 p = uv * vec2(uResolution.x / uResolution.y, 1.0);",
    "",
    "  vec2 warp = vec2(",
    "    wave(p * 2.2, 1.6, 1.0, t),",
    "    wave(p * 2.6 + 5.2, 1.9, 1.2, t)",
    "  ) * 0.35;",
    "",
    "  float layer1 = wave(p + warp, 3.0, 1.0, t);",
    "  float layer2 = wave(p * 1.7 - warp * 1.3, 4.5, 1.4, t + 10.0);",
    "  float layer3 = wave(p * 0.9 + warp * 0.6, 1.2, 0.6, t + 20.0);",
    "",
    "  float light = (layer1 * 0.5 + layer2 * 0.35 + layer3 * 0.4);",
    "  light = pow(max(light, 0.0), 1.6);",
    "",
    "  vec3 base = mix(uColorDeep, uColorSapphire, smoothstep(0.0, 1.0, uv.y));",
    "  vec3 col = mix(base, uColorGold, clamp(light * 1.4, 0.0, 1.0));",
    "",
    "  float vig = smoothstep(1.1, 0.2, length(uv - 0.5));",
    "  col *= mix(0.55, 1.05, vig);",
    "",
    "  float grain = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);",
    "  col += (grain - 0.5) * 0.015;",
    "",
    "  gl_FragColor = vec4(col, 1.0);",
    "}",
  ].join("\n");

  function hexToVec3(hex) {
    var c = new THREE.Color(hex);
    return new THREE.Vector3(c.r, c.g, c.b);
  }

  var hero = canvas.closest(".hero");
  var renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    alpha: false,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  var scene = new THREE.Scene();
  var camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  var uniforms = {
    uTime: { value: 0 },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uColorDeep: { value: hexToVec3(0x17242c) },
    uColorSapphire: { value: hexToVec3(0x2c4a5e) },
    uColorGold: { value: hexToVec3(0xb08d4f) },
    uScroll: { value: 0 },
  };

  var material = new THREE.ShaderMaterial({
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    uniforms: uniforms,
  });

  var mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  scene.add(mesh);

  function resize() {
    var w = hero.clientWidth;
    var h = hero.clientHeight;
    renderer.setSize(w, h, false);
    uniforms.uResolution.value.set(w, h);
  }

  var scrollTarget = 0;
  var scrollCurrent = 0;

  function onScroll() {
    var heroHeight = hero.offsetHeight || 1;
    var progress = Math.min(Math.max(-hero.getBoundingClientRect().top / heroHeight, 0), 1);
    scrollTarget = progress;

    // fade + lift the canvas itself as the visitor scrolls past the hero
    canvas.style.transform = "translateY(" + progress * 60 + "px)";
    canvas.style.opacity = String(1 - progress * 0.5);
  }

  window.addEventListener("resize", resize);
  window.addEventListener("scroll", onScroll, { passive: true });
  resize();
  onScroll();

  var clock = new THREE.Clock();

  function renderStaticFrame() {
    uniforms.uTime.value = 2.4;
    renderer.render(scene, camera);
  }

  if (prefersReducedMotion) {
    renderStaticFrame();
    return;
  }

  function animate() {
    uniforms.uTime.value = clock.getElapsedTime();
    scrollCurrent += (scrollTarget - scrollCurrent) * 0.08;
    uniforms.uScroll.value = scrollCurrent;
    renderer.render(scene, camera);
    requestAnimationFrame(animate);
  }
  requestAnimationFrame(animate);
})();
